import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { SignUpLink1 } from '../types';
import { sendMail } from '../services/email/sendMail';

interface SendSignUpLinkOptions {
  signUpLink: SignUpLink1;
  fromName?: string;
  customMessage?: string;
}

/**
 * Validates an email address format
 * @param email The email address to validate
 * @returns boolean indicating if the email is valid
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Sends a signup invitation email
 *
 * @param options Options containing the signup link and optional customization
 * @throws Error if the email address is invalid or if sending fails
 */
export async function sendSignUpLink({
  signUpLink,
  fromName = 'MilestoneMap.org',
  customMessage,
}: SendSignUpLinkOptions): Promise<void> {
  // Validate email address
  if (!signUpLink.toEmail || !isValidEmail(signUpLink.toEmail)) {
    throw new Error(`Invalid email address: ${signUpLink.toEmail}`);
  }

  // Include the fromName in the message content
  const messageText =
    customMessage ||
    `${fromName} has invited you to collaborate on MilestoneMap.org!
    
    Click this link to create an account: ${signUpLink.url}
    
    This invitation link will expire in 7 days.`;

  // HTML version of the message (optional but recommended)
  const messageHtml =
    customMessage ||
    `<p>${fromName} has invited you to collaborate on MilestoneMap.org!</p>
    <p><a href="${signUpLink.url}">Click here to create an account</a></p>
    <p>This invitation link will expire in 7 days.</p>`;

  try {
    // First update signup link to show we're attempting to send
    const signUpLinkRef = doc(db, 'signUpLinks', signUpLink.signUpCode);
    await updateDoc(signUpLinkRef, {
      status: 'pending',
    });

    // Send the email using our new service
    const result = await sendMail({
      to: signUpLink.toEmail,
      subject: 'MilestoneMap.org Signup Link',
      text: messageText,
      html: messageHtml,
    });

    // Update signup link based on email result
    await updateDoc(signUpLinkRef, {
      status: result.status,
      ...(result.error && { error: result.error }),
      ...(result.status === 'sent' && { sentDate: result.sentAt }),
    });

    if (result.status === 'error') {
      throw new Error(result.error || 'Failed to send signup link email');
    }
  } catch (error) {
    console.error('Error sending signup link:', error);

    // Update signup link status to error
    const signUpLinkRef = doc(db, 'signUpLinks', signUpLink.signUpCode);
    await updateDoc(signUpLinkRef, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Failed to send email',
    });

    throw error;
  }
}
